import {Subscription} from "rxjs";
import {Router} from "@angular/router";
import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";

import {ToastrService} from "ngx-toastr";
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";

import {appRoutes} from "@numizmat/constants";
import {PurchaseApiService} from "@numizmat/services/core";
import {CurrentUser} from "@numizmat/services-models/account";
import {UserConfirmationMethod} from "@numizmat/enumerations";
import {CurrentUserStateService} from "@numizmat/services/account";
import {InsertPurchaseRequest, PublicAdDetailResponse} from "@numizmat/services-models/core";
import {AdDetailStateService} from "@numizmat/services/core/ad-detail/ad-detail-state.service";

@Component({
  selector: 'nz-purchase-confirm-modal',
  standalone: true,
  imports: [
    ReactiveFormsModule
  ],
  templateUrl: './purchase-confirm-modal.component.html',
  styleUrl: './purchase-confirm-modal.component.scss'
})
export class PurchaseConfirmModalComponent implements OnInit, OnDestroy {
  private router: Router = inject(Router);
  private activeModal: NgbActiveModal = inject(NgbActiveModal);
  private toastrService: ToastrService = inject(ToastrService);
  private purchaseApiService: PurchaseApiService = inject(PurchaseApiService);
  private adDetailStateService: AdDetailStateService = inject(AdDetailStateService);
  private currentUserStateService: CurrentUserStateService = inject(CurrentUserStateService);

  private subs: Subscription = new Subscription();

  protected errorMessage?: string;
  protected loading: boolean = false;
  protected currentUser!: CurrentUser;
  protected adDetails!: PublicAdDetailResponse;
  protected form: FormGroup = this.initializeForm();

  protected readonly appRoutes = appRoutes;
  protected readonly UserConfirmationMethod = UserConfirmationMethod;

  public ngOnInit(): void {
    this.getCurrentUser();
    this.getPublicAdDetails();
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  protected onSubmit() {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }
    this.loading = true;
    const request: InsertPurchaseRequest = {
      adId: this.adDetails.adId,
      pin: this.pin?.value,
      password: this.password?.value
    };
    this.purchaseApiService.insertPurchase(request).subscribe({
      next: response => {
        this.loading = false;
        if (response.success) {
          this.toastrService.success(response.successMessage);
          this.closeModal();
        } else {
          this.errorMessage = response.errorMessage;
        }
      },
      error: error => {
        this.closeModal();
      }
    });
  }

  protected closeModal(route?: string[]): void {
    this.activeModal.dismiss();

    if (!!route) {
      setTimeout(() => {
        this.router.navigate(route).then();
      }, 100);
    }
  }

  private initializeForm() {
    return new FormGroup({
      password: new FormControl(null, [Validators.required]),
      pin: new FormControl(null, [Validators.required])
    });
  }

  private getCurrentUser(): void {
    const subscription: Subscription = this.currentUserStateService.getCurrentUser().subscribe({
      next: currentUser => {
        this.currentUser = currentUser;
        if (this.currentUser.userData) {
          if (this.currentUser.userData.confirmationMethod === UserConfirmationMethod.Pin) {
            this.pin?.enable();
            this.password?.disable();
          } else if (this.currentUser.userData.confirmationMethod === UserConfirmationMethod.Password) {
            this.pin?.disable();
            this.password?.enable();
          } else {
            this.pin?.disable();
            this.password?.disable();
          }
        }
      }
    });
    this.subs.add(subscription);
  }

  private getPublicAdDetails(): void {
    this.adDetailStateService.getPublicAdDetails().subscribe({
      next: (adDetails: PublicAdDetailResponse | undefined) => {
        if (!!adDetails) {
          this.adDetails = adDetails;
        }
      }
    });
  }

  get password() {
    return this.form.get('password');
  }

  get pin() {
    return this.form.get('pin');
  }
}
